import { useState, useEffect } from 'react';

interface ExitIntentPopupProps {
  onSubscribe: (email: string) => Promise<void>;
}

export default function ExitIntentPopup({ onSubscribe }: ExitIntentPopupProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Check if popup was already shown in this session
    const shown = sessionStorage.getItem('exitIntentShown');
    if (shown) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if mouse leaves from top of page and hasn't been shown yet
      if (e.clientY <= 0 && !hasShown && !isVisible) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitIntentShown', 'true');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasShown, isVisible]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setMessage('');

    try {
      await onSubscribe(email);
      setMessage('Success! Check your email for your free guide.');
      setEmail('');
      setTimeout(() => setIsVisible(false), 3000);
    } catch (error) {
      setMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 relative animate-[slideUp_0.3s_ease-out]">
        <button
          onClick={() => setIsVisible(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close popup"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-gift-line text-3xl text-teal-600"></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Wait! Don't Miss Your Free Guide
          </h3>
          <p className="text-gray-600">
            Get our comprehensive "Moving to Ireland Checklist" - a $29 value, absolutely free!
          </p>
        </div>

        <div className="bg-teal-50 rounded-lg p-4 mb-6">
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 mt-0.5 mr-2"></i>
              <span>Step-by-step relocation timeline</span>
            </li>
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 mt-0.5 mr-2"></i>
              <span>Visa application tips from experts</span>
            </li>
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 mt-0.5 mr-2"></i>
              <span>Cost of living calculator</span>
            </li>
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 mt-0.5 mr-2"></i>
              <span>Housing search strategies</span>
            </li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            required
            disabled={isSubmitting}
          />
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? 'Sending...' : 'Get My Free Guide'}
          </button>

          {message && (
            <p className={`text-sm text-center ${message.includes('Success') ? 'text-teal-600' : 'text-red-600'}`}>
              {message}
            </p>
          )}
        </form>

        <p className="text-xs text-gray-500 text-center mt-4">
          Join 5,000+ Americans successfully relocating to Ireland
        </p>
      </div>
    </div>
  );
}